import { supabase } from "../integrations/supabase/client";
import type { Json } from "../integrations/supabase/types";
import { typedSupabase } from "../lib/typed-supabase";
import { logger } from "../lib/logger";
import { IService } from "./service-registry";
import type {
  GamificationStats,
  LeaderboardEntry,
  LeaderboardPeriod,
  XpTransaction,
} from "../types/gamification";

// Create a gamification-specific supabase client for RPC calls
const gamificationRPC = supabase;

// Declare gtag for Google Analytics
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Feature flags for progressive enhancement
const FEATURE_FLAGS = {
  enableCaching: true,
  enablePerformanceMonitoring: true,
  enableRetryLogic: true,
  enableDebouncing: true,
  enablePrefetching: false, // Can be enabled later
};

// Performance monitoring interface
interface PerformanceMetrics {
  startTime: number;
  endTime: number;
  duration: number;
  cacheHit: boolean;
  period: LeaderboardPeriod;
  error?: Error;
}

// Cache entry interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// Enhanced error types
interface GamificationError extends Error {
  type:
    | "network"
    | "empty_data"
    | "invalid_period"
    | "server_error"
    | "timeout";
  retryable: boolean;
  fallbackAction?: string;
}

class PerformanceMonitor {
  static trackLeaderboardLoad(metrics: PerformanceMetrics) {
    if (!FEATURE_FLAGS.enablePerformanceMonitoring) return;

    // Log performance metrics
    logger.info(
      `Leaderboard ${metrics.period}: ${metrics.duration}ms (cache: ${metrics.cacheHit ? "hit" : "miss"})`
    );

    // Warn about slow performance
    if (metrics.duration > 1000) {
      logger.warn(
        `Slow leaderboard load for ${metrics.period}: ${metrics.duration}ms`
      );
    }

    // Track in analytics if available
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "leaderboard_load", {
        period: metrics.period,
        duration_ms: Math.round(metrics.duration),
        cache_hit: metrics.cacheHit,
        custom_map: { metric1: "leaderboard_performance" },
      });
    }
  }

  static trackError(period: LeaderboardPeriod, error: GamificationError) {
    if (!FEATURE_FLAGS.enablePerformanceMonitoring) return;

    logger.error(`Leaderboard error for ${period}:`, error);

    // Track errors in analytics
    if (typeof window.gtag !== "undefined") {
      window.gtag("event", "leaderboard_error", {
        period,
        error_type: error.type,
        retryable: error.retryable,
      });
    }
  }
}

class LeaderboardCache {
  private cache = new Map<string, CacheEntry<LeaderboardEntry[]>>();

  // TTL strategies based on data update frequency
  private readonly CACHE_TTL = {
    day: 60000, // 1 minute for daily (frequent updates)
    week: 300000, // 5 minutes for weekly
    month: 900000, // 15 minutes for monthly
    all_time: 1800000, // 30 minutes for all-time
  };

  get(key: string): LeaderboardEntry[] | undefined {
    if (!FEATURE_FLAGS.enableCaching) return undefined;

    const entry = this.cache.get(key);
    if (!entry) return undefined;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return undefined;
    }

    return entry.data;
  }

  set(key: string, data: LeaderboardEntry[], period: LeaderboardPeriod): void {
    if (!FEATURE_FLAGS.enableCaching) return;

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: this.CACHE_TTL[period],
    });
  }

  invalidate(period?: LeaderboardPeriod): void {
    if (period) {
      const keysToDelete = Array.from(this.cache.keys()).filter((key) =>
        key.startsWith(period)
      );
      keysToDelete.forEach((key) => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Debounce utility for preventing rapid-fire requests
function debounce<T extends (...args: never[]) => Promise<unknown>>(
  func: T,
  delay: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>): Promise<ReturnType<T>> => {
    clearTimeout(timeoutId);
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await func(...args);
          resolve(result as ReturnType<T>);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
}

export class GamificationService implements IService {
  public readonly name = "gamification" as const;
  private readonly supabase = typedSupabase;
  private cache = new LeaderboardCache();
  private requestsInFlight = new Map<string, Promise<LeaderboardEntry[]>>();
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
  }

  async dispose(): Promise<void> {
    this.initialized = false;
  }

  // Create error with enhanced information
  private createError(
    message: string,
    type: GamificationError["type"],
    retryable = true,
    fallbackAction?: string
  ): GamificationError {
    const error = new Error(message) as GamificationError;
    error.type = type;
    error.retryable = retryable;
    error.fallbackAction = fallbackAction;
    return error;
  }

  // Validate period parameter
  private validatePeriod(period: LeaderboardPeriod): void {
    const validPeriods: LeaderboardPeriod[] = [
      "day",
      "week",
      "month",
      "all_time",
    ];
    if (!validPeriods.includes(period)) {
      throw this.createError(
        `Invalid period: ${period}. Must be one of: ${validPeriods.join(", ")}`,
        "invalid_period",
        false
      );
    }
  }

  // Enhanced retry logic with exponential backoff
  private async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries = 3,
    baseDelay = 1000
  ): Promise<T> {
    if (!FEATURE_FLAGS.enableRetryLogic) {
      return operation();
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        const gamificationError = error as GamificationError;

        if (attempt === maxRetries || !gamificationError.retryable) {
          throw error;
        }

        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    throw new Error("Max retries exceeded");
  }

  // Categorize Supabase errors for better handling
  private categorizeSupabaseError(error: {
    code?: string;
    message?: string;
  }): GamificationError["type"] {
    if (
      error.code === "PGRST116" ||
      error.message?.includes("Invalid period")
    ) {
      return "invalid_period";
    }
    if (error.code === "PGRST301" || error.message?.includes("timeout")) {
      return "timeout";
    }
    return "server_error";
  }

  /**
   * Award XP to a user
   * @deprecated Use `awardXp` instead
   */
  async awardRizz(
    userId: string,
    xpAmount: number,
    transactionType: string,
    taskId?: string,
    transactionDetails?: Record<string, unknown>
  ): Promise<{ success: boolean; error?: Error }> {
    return this.awardXp(userId, xpAmount, transactionType, taskId, transactionDetails);
  }

  /**
   * Award XP to a user
   */
  async awardXp(
    userId: string,
    xpAmount: number,
    transactionType: string,
    taskId?: string,
    transactionDetails?: Record<string, unknown>
  ): Promise<{ success: boolean; error?: Error }> {
    try {
      if (xpAmount <= 0) {
        return {
          success: false,
          error: new Error("XP amount must be positive"),
        };
      }

      const { error } = await this.supabase
        .rpc("award_xp_with_season", {
          user_id: userId,
          xp_amount: xpAmount,
          transaction_type: transactionType,
          task_id: taskId || undefined,
          details: (transactionDetails || undefined) as Json,
        })
        .single();

      if (error) {
        logger.error("Error awarding XP:", error);
        return { success: false, error: new Error(error.message) };
      }

      return { success: true };
    } catch (error) {
      logger.error("Exception awarding XP:", error);
      const err = error instanceof Error ? error : new Error("Unknown error");
      return {
        success: false,
        error: err,
      };
    }
  }

  /**
   * Get user's gamification stats
   */
  async getUserStats(userId: string): Promise<GamificationStats | undefined> {
    try {
      const { data, error } = await gamificationRPC
        // gamification RPC functions not in generated types
        .rpc("get_user_gamification_stats", {
          p_user_id: userId,
        })
        .single();

      if (error) {
        logger.error("Error fetching user stats:", error);
        return undefined;
      }

      if (!data) {
        return undefined;
      }

      // Type assertion for the RPC return type
      const stats = data as {
        total_xp: number;
        current_streak: number;
        xp_today: number;
        xp_this_week: number;
        xp_this_month: number;
        longest_streak: number;
        tasks_completed: number;
        last_task_completed_at: string | null;
      };

      return {
        totalXp: stats.total_xp,
        currentStreak: stats.current_streak,
        xpToday: stats.xp_today,
        xpThisWeek: stats.xp_this_week,
        xpThisMonth: stats.xp_this_month,
        longestStreak: stats.longest_streak,
        tasksCompleted: stats.tasks_completed,
        lastTaskCompletedAt: stats.last_task_completed_at || undefined,
      };
    } catch (error) {
      logger.error("Exception fetching user stats:", error);
      return undefined;
    }
  }

  /**
   * Get XP transaction history for a user
   */
  async getUserTransactionsAdmin(
    userId: string,
    limit: number = 500
  ): Promise<XpTransaction[]> {
    try {
      const { data, error } = await gamificationRPC
        .rpc("get_user_transactions_admin", {
          target_user_id: userId,
          max_rows: limit,
        })
        .order("created_at", { ascending: false });

      if (error) {
        logger.error("Error fetching XP transactions:", error);
        return [];
      }

      return (data as XpTransaction[]) || [];
    } catch (error) {
      logger.error("Exception fetching XP transactions:", error);
      return [];
    }
  }

  /**
   * Get leaderboard data for a specific period
   */
  async getLeaderboard(
    period: LeaderboardPeriod = "all_time",
    limit: number = 10
  ): Promise<LeaderboardEntry[]> {
    this.validatePeriod(period);
    const cacheKey = `${period}-${limit}`;

    // Return cached data if available
    const cachedData = this.cache.get(cacheKey);
    if (cachedData) {
      PerformanceMonitor.trackLeaderboardLoad({
        startTime: 0,
        endTime: 0,
        duration: 0,
        cacheHit: true,
        period,
      });
      return cachedData;
    }

    // Handle requests in flight
    if (this.requestsInFlight.has(cacheKey)) {
      return this.requestsInFlight.get(cacheKey)!;
    }

    const requestPromise = this.retryWithBackoff(async () => {
      const startTime = performance.now();
      try {
        const { data, error } = await gamificationRPC.rpc("get_leaderboard", {
          period: period,
          limit_count: limit,
        });

        if (error) {
          const gamificationError = this.createError(
            `Failed to fetch leaderboard: ${error.message}`,
            this.categorizeSupabaseError(error)
          );
          PerformanceMonitor.trackError(period, gamificationError);
          throw gamificationError;
        }

        if (!data) {
          const gamificationError = this.createError(
            `No data returned for ${period} leaderboard`,
            "empty_data",
            false
          );
          PerformanceMonitor.trackError(period, gamificationError);
          throw gamificationError;
        }

        const leaderboardData: LeaderboardEntry[] = (
          data as Array<{
            user_id: string;
            username: string;
            full_name: string;
            avatar_url: string;
            xp: number;
            xp_this_week: number;
            xp_this_month: number;
            current_streak: number;
            longest_streak: number;
            tasks_completed: number;
            rank: number;
          }>
        ).map((item) => ({
          user_id: item.user_id,
          username: item.username,
          full_name: item.full_name,
          display_name: item.full_name || item.username,
          avatar_url: item.avatar_url,
          xp: item.xp,
          xp_this_week: item.xp_this_week,
          xp_this_month: item.xp_this_month,
          current_streak: item.current_streak,
          longest_streak: item.longest_streak,
          tasks_completed: item.tasks_completed,
          rank: item.rank,
        }));

        this.cache.set(cacheKey, leaderboardData, period);

        PerformanceMonitor.trackLeaderboardLoad({
          startTime,
          endTime: performance.now(),
          duration: performance.now() - startTime,
          cacheHit: false,
          period,
        });

        return leaderboardData;
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Unknown error during leaderboard fetch");
        PerformanceMonitor.trackError(period, err as GamificationError);
        throw err;
      } finally {
        this.requestsInFlight.delete(cacheKey);
      }
    });

    this.requestsInFlight.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Calculate XP for a completed task (server-side)
   * @deprecated Use `calculateTaskXp` instead
   */
  async calculateTaskRizz(taskId: string): Promise<number> {
    return this.calculateTaskXp(taskId);
  }

  /**
   * Calculate XP for a completed task (server-side)
   */
  async calculateTaskXp(taskId: string): Promise<number> {
    try {
      const { data, error } = await this.supabase.rpc("calculate_task_xp", {
        task_id: taskId,
      });

      if (error) {
        logger.error("Error calculating task XP:", error);
        return 0;
      }

      return (data as number) || 0;
    } catch (error) {
      logger.error("Exception calculating task XP:", error);
      return 0;
    }
  }

  /**
   * Check for and award special achievements based on task completion
   */
  async checkSpecialAchievements(taskId: string, userId: string) {
    try {
      // This RPC call will handle all achievement logic within the database
      const { data, error } = await this.supabase.rpc(
        "check_special_achievements",
        {
          task_id: taskId,
          user_id: userId,
        }
      );

      if (error) {
        logger.error("Error checking special achievements:", error);
        return [];
      }

      // data is expected to be an array of achievement names or similar
      return data || [];
    } catch (error) {
      logger.error("Exception checking special achievements:", error);
      return [];
    }
  }

  /**
   * Reset periodic XP for all users
   * @deprecated Use `resetPeriodicXp` instead
   */
  async resetPeriodicRizz(
    period: "day" | "week" | "month"
  ): Promise<{ success: boolean; error?: Error }> {
    return this.resetPeriodicXp(period);
  }

  /**
   * Reset periodic XP for all users
   */
  async resetPeriodicXp(
    period: "day" | "week" | "month"
  ): Promise<{ success: boolean; error?: Error }> {
    try {
      const { error } = await this.supabase.rpc("reset_periodic_xp", {
        period: period,
      });

      if (error) {
        logger.error(`Error resetting ${period} XP:`, error);
        return { success: false, error: new Error(error.message) };
      }

      // Invalidate relevant cache entries after reset
      this.cache.invalidate(period);

      return { success: true };
    } catch (error) {
      logger.error(`Exception resetting ${period} XP:`, error);
      const err = error instanceof Error ? error : new Error("Unknown error");
      return {
        success: false,
        error: err,
      };
    }
  }

  /**
   * Get a user's rank on the leaderboard
   */
  async getUserRank(
    userId: string,
    period: "all_time" | "week" | "month" | "day" = "all_time"
  ): Promise<number> {
    this.validatePeriod(period);
    try {
      const { data, error } = await gamificationRPC
        // gamification RPC functions not in generated types
        .rpc("get_user_rank", {
          p_user_id: userId,
          p_period: period,
        });

      if (error) {
        logger.error("Error getting user rank:", error);
        return 0; // Return 0 or appropriate default on error
      }
      return (data as number) || 0;
    } catch (error) {
      logger.error("Exception getting user rank:", error);
      return 0;
    }
  }

  /**
   * Get top performers for a given period
   */
  async getTopPerformers(
    period: "week" | "month" | "day",
    limit: number = 3
  ): Promise<LeaderboardEntry[]> {
    this.validatePeriod(period);
    try {
      const { data, error } = await gamificationRPC
        // gamification RPC functions not in generated types
        .rpc("get_top_performers", {
          p_period: period,
          p_limit: limit,
        });

      if (error) {
        logger.error("Error fetching top performers:", error);
        return [];
      }
      return (data as LeaderboardEntry[]) || [];
    } catch (error) {
      logger.error("Exception fetching top performers:", error);
      return [];
    }
  }

  /**
   * Calculate streak bonus for a user
   */
  async calculateStreakBonus(userId: string): Promise<number> {
    try {
      const { data, error } = await this.supabase.rpc(
        "calculate_streak_bonus",
        {
          user_id: userId,
        }
      );

      if (error) {
        logger.error("Error calculating streak bonus:", error);
        return 0;
      }

      return (data as number) || 0;
    } catch (error) {
      logger.error("Exception calculating streak bonus:", error);
      return 0;
    }
  }

  // Public API methods that expose internal logic
  // Debounced version of getLeaderboard to prevent excessive calls
  getDebouncedLeaderboard = debounce(
    (period: LeaderboardPeriod = "all_time", limit: number = 10) =>
      this.getLeaderboard(period, limit),
    500
  );

  /**
   * Invalidate specific or all cache entries
   */
  invalidateCache(period?: LeaderboardPeriod): void {
    this.cache.invalidate(period);
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Pre-fetches leaderboard data for specified periods.
   * This helps to warm up the cache and improve initial load times.
   */
  async prefetchLeaderboards(
    periods: LeaderboardPeriod[] = ["day", "week", "month", "all_time"]
  ): Promise<void> {
    if (!FEATURE_FLAGS.enablePrefetching) return;

    logger.info("⚡ Prefetching leaderboards...");
    try {
      await Promise.all(
        periods.map((period) => this.getLeaderboard(period, 10)) // Prefetch top 10 for each period
      );
      logger.info("✅ Leaderboards prefetched successfully.");
    } catch (error) {
      logger.error("❌ Failed to prefetch leaderboards:", error);
    }
  }
}

export const gamificationService = new GamificationService();
